import { createContext, FC, PropsWithChildren, useEffect, useState } from "react";
import { NavigationContextData } from "../@types/NavContextData";
import { useLocation } from "react-router-dom";
import { RouteType } from "../@types/RouteType";
import { useTour } from "@reactour/tour";
import useAuth from "../hooks/useAuthentication";
import { getMatchingRoute } from "../utils/getMatchingRoute";

export const NavigationContext = createContext<NavigationContextData | null>(null);

export const NavigationProvider: FC<PropsWithChildren> = ({ children }) => {
    const { user, userAuthorizedRoutes } = useAuth();
    const location = useLocation();
    const [currentRoute, setCurrentRoute] = useState<RouteType | undefined>(undefined);
    const [routeDisplayText, setRouteDisplayText] = useState<string>('Dashboard');
    const [navExpanded, setNavExpanded] = useState<boolean>(true);
    const { setSteps } = useTour();

    /*
        Handle setting tour steps based on the current route
    */
    useEffect(() => {
        if (currentRoute && setSteps) {
            if (currentRoute.path === '/dashboard' && currentRoute.hasTutorial && currentRoute.tutorialSteps) {
                switch (user?.attributes.type) {
                    case 'dealer-sales':
                        setSteps([...currentRoute.tutorialSteps.slice(2, 2)]);
                        break;
                    case 'dealer-service':
                        const indexesToRemove = [1, 2, 6, 7]
                        const modifiedSteps = currentRoute.tutorialSteps
                            .filter((route, index) => indexesToRemove.indexOf(index) === -1);
                        setSteps(modifiedSteps);
                        break;
                    default:
                        setSteps(currentRoute.tutorialSteps);
                        break;
                }
            } else if (currentRoute.hasTutorial && currentRoute.tutorialSteps) {
                setSteps(currentRoute.tutorialSteps);
            } else {
                setSteps([]);
            }
        }
    }, [
        currentRoute,
        user,
        setSteps
    ]);

    useEffect(() => {
        
        // Scroll to top of page on route change
        window.scrollTo(0, 0);

        const matchingRoute: RouteType | undefined = getMatchingRoute(userAuthorizedRoutes, location);

        if (matchingRoute) {

            setCurrentRoute(matchingRoute);

            setRouteDisplayText(matchingRoute.displayText);

        }

    }, [
        user,
        location,
        userAuthorizedRoutes,
        setRouteDisplayText
    ]);

    return (
        <NavigationContext.Provider
            value={{
                currentRoute,
                setCurrentRoute,
                routeDisplayText,
                setRouteDisplayText,
                navExpanded,
                setNavExpanded
            }}>
            {children}
        </NavigationContext.Provider>
    )
}